import { cloneElement, isValidElement, useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { useSharedText } from '../lib/i18n';
import { HelpTip } from './Page';
import { ConfirmDialog } from './Dialog';

interface FieldProps {
  label: ReactNode;
  /** Short text under the control that the merchant needs to fill it in. */
  hint?: ReactNode;
  /** Background (what it is, typical values) in an InfoTip beside the label. */
  help?: ReactNode;
  error?: string | null;
  optional?: boolean;
  className?: string;
  /** Single form control; receives id, aria-describedby and aria-invalid. */
  children: ReactElement<Record<string, unknown>>;
}

export function Field({ label, hint, help, error, optional, className, children }: FieldProps) {
  const autoId = useId();
  const text = useSharedText();
  const childId = (isValidElement(children) && typeof children.props.id === 'string' ? children.props.id : undefined) ?? `f${autoId}`;
  const hintId = hint ? `${childId}-hint` : undefined;
  const errorId = error ? `${childId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = cloneElement(children, {
    id: childId,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
  });
  const labelEl = (
    <label className="field__label" htmlFor={childId}>
      {label}
      {optional ? <span className="field__optional">{text.optional}</span> : null}
    </label>
  );
  return (
    <div className={`field ${className ?? ''}`}>
      {help ? (
        // The tip sits next to the label, not inside it: a button inside a <label> would steal its clicks.
        <div className="title-row">
          {labelEl}
          <HelpTip help={help} topic={label} />
        </div>
      ) : (
        labelEl
      )}
      {control}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId}>
          <AlertCircle size={14} aria-hidden="true" />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface SummaryItem {
  field?: string;
  message: string;
}

/** Focuses itself when shown so keyboard/screen reader users land on the problems. */
export function ErrorSummary({ title, items }: { title?: string; items: SummaryItem[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const text = useSharedText();
  const signature = items.map((i) => `${i.field}:${i.message}`).join('|');
  useEffect(() => {
    if (items.length) ref.current?.focus();
  }, [signature, items.length]);
  if (!items.length) return null;
  return (
    <div className="error-summary" role="alert" tabIndex={-1} ref={ref}>
      <strong>{title ?? text.fixErrors}</strong>
      <ul>
        {items.map((item, index) => (
          <li key={index}>
            {item.field ? (
              <a
                href={`#${item.field}`}
                onClick={(event) => {
                  const el = document.getElementById(item.field ?? '');
                  if (el) {
                    event.preventDefault();
                    el.focus();
                    el.scrollIntoView({ block: 'center' });
                  }
                }}
              >
                {item.message}
              </a>
            ) : (
              item.message
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" id={id} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="check__text">
        {label}
        {description ? <small>{description}</small> : null}
      </span>
    </label>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <label className="check">
      <input type="checkbox" id={id} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="check__text">
        {label}
        {description ? <small>{description}</small> : null}
      </span>
    </label>
  );
}

/** Input with a trailing/leading unit; forwards id/aria props from Field to the input. */
export function UnitInput({
  unit,
  leading,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { unit: string; leading?: boolean }) {
  const input = <input {...props} className={className ?? 'input input--mono'} />;
  return (
    <div className="input-group">
      {leading ? <span className="input-addon" aria-hidden="true">{unit}</span> : null}
      {input}
      {!leading ? <span className="input-addon" aria-hidden="true">{unit}</span> : null}
    </div>
  );
}

/** Integer parsing for numeric inputs kept as strings while editing. */
export function parseIntStrict(value: string): number | null {
  const s = value.trim();
  if (!s) return null;
  if (!/^-?\d+$/.test(s)) return Number.NaN;
  return Number(s);
}

export function parseDecimal(value: string): number | null {
  const s = value.trim();
  if (!s) return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return Number.NaN;
  return Number(s);
}

/**
 * The settings pages' Save / Discard row. Discard asks first, then puts the form back to the last
 * saved values on the same page (`onReset`).
 */
export function SaveBar({
  dirty,
  pending,
  onReset,
  submitLabel = 'Save changes',
  status,
  children,
}: {
  dirty: boolean;
  pending: boolean;
  onReset?: () => void;
  submitLabel?: string;
  status?: ReactNode;
  /** Other buttons, before Discard. */
  children?: ReactNode;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <div className="form-actions">
      <span className="form-actions__status" aria-live="polite">
        {status ?? (pending ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved')}
      </span>
      {children}
      {onReset ? (
        <button type="button" className="btn btn--ghost" onClick={() => setAsking(true)} disabled={!dirty || pending}>
          Discard
        </button>
      ) : null}
      <button type="submit" className="btn btn--primary" disabled={!dirty || pending}>
        {pending ? 'Saving…' : submitLabel}
      </button>
      {onReset ? (
        <ConfirmDialog
          open={asking && dirty}
          title="Discard unsaved changes?"
          body={<p>Back to your last save.</p>}
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          danger
          onClose={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false);
            onReset();
          }}
        />
      ) : null}
    </div>
  );
}
